import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  BookOpenIcon,
  PencilIcon,
  CalculatorIcon,
  BeakerIcon,
  GlobeAltIcon,
  HeartIcon,
  UserGroupIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const Academics = () => {
  return (
    <main id="main-content" className="bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 bg-[#4a0402] overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-white text-[#630503] px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Education Excellence
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Academics
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              At PS 011X, we strive to ensure that our students receive the best education possible. All of our programs are aligned to the Next Generation Learning Standards.
            </p>
          </div>
        </div>
      </section>

      {/* Core Academic Programs */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Core Curriculum
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Our Academic Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our comprehensive curriculum offers multiple entry points so all learners are successful.
            </p>
          </div>

          {/* Academic Subjects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-20">
            {/* Reading */}
            <div className="bg-blue-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-blue-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <BookOpenIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Reading</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Our literacy program is built into Reading, a research-based program that develops a culture of learning and inquiry.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Students dive into Reading goals as accessible via TeachHub.
              </p>
            </div>

            {/* Writing */}
            <div className="bg-purple-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-purple-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <PencilIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Writing</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Our writing program is also through HMH Into Reading. Our students write in the three major genres.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Focus on narrative, opinion, and informational writing.
              </p>
            </div>

            {/* Mathematics */}
            <div className="bg-green-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-green-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <CalculatorIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Mathematics</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Our math program is Envision 2020. It is both student friendly and has a strong digital component.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Focus on mathematical understanding and problem solving.
              </p>
            </div>

            {/* Science */}
            <div className="bg-orange-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-orange-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <BeakerIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Science</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Our students are engaged throughout the year with Amplify Science curriculum.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Hands-on experiments through rigorous curriculum aligned to Next Generation Science Standards.
              </p>
            </div>

            {/* Social Studies */}
            <div className="bg-teal-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <GlobeAltIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Social Studies</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                We use the NYC Passport Social Studies program, a curriculum designed to provide authentic learning experiences.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Focus on primary resources and critical thinking skills.
              </p>
            </div>

            {/* Physical Education */}
            <div className="bg-red-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <HeartIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Physical Education</h3>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Our students participate in physical and health education several times a week.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We focus on teaching them the essentials to maintain healthy bodies inside and out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Student Council Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Student Leadership
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Student Council
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Student council is the voice of the students at PS 011X. The students meet monthly with Ms. Rosario to discuss ways to improve the school.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Student Council Image */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <img 
                src="https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="Student Council Meeting" 
                className="w-full h-80 object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>

            {/* Student Council Info */}
            <div className="bg-white rounded-xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="mb-8">
                <div className="bg-[#630503] p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6 shadow-md">
                  <UserGroupIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Student Voice & Leadership</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  Our Student Council empowers young leaders to make meaningful contributions to their school community. 
                  Students develop leadership skills, practice democratic processes, and advocate for their peers.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg mb-8">
                  Through monthly meetings and collaborative projects, council members work directly with school 
                  administration to implement positive changes and improvements.
                </p>
                <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#630503]">
                  <h4 className="font-bold text-gray-900 mb-2">Council Activities Include:</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li>• School improvement initiatives</li>
                    <li>• Community service projects</li>
                    <li>• Student advocacy and representation</li>
                    <li>• Leadership skill development</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Excellence Section */}
      <section className="py-24 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-white text-[#630503] px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Academic Achievement
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-8 leading-tight">
              Excellence in Education
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Our commitment to academic excellence ensures every student reaches their full potential through innovative teaching and comprehensive support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Academic Standards */}
            <div className="bg-white/10 rounded-xl p-10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <AcademicCapIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Next Generation Standards</h3>
                <p className="text-white/90 leading-relaxed">
                  All programs aligned to Next Generation Learning Standards ensuring rigorous, relevant education for every student.
                </p>
              </div>
            </div>

            {/* Multiple Entry Points */}
            <div className="bg-white/10 rounded-xl p-10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <UserGroupIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Inclusive Learning</h3>
                <p className="text-white/90 leading-relaxed">
                  Multiple entry points and differentiated instruction ensure all learners can access and succeed in our curriculum.
                </p>
              </div>
            </div>

            {/* Research-Based Programs */}
            <div className="bg-white/10 rounded-xl p-10 backdrop-blur-sm hover:bg-white/15 transition-all duration-300">
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <BookOpenIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Research-Based Curriculum</h3>
                <p className="text-white/90 leading-relaxed">
                  Evidence-based programs and teaching methods proven to enhance student learning and academic achievement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Academics; 